import React from 'react';
import { Check, X, AlertTriangle } from 'lucide-react';
import { scorePassword } from '../utils/passwordStrength';

/**
 * Live password strength indicator.
 * Shows a 4-segment bar + current label + criteria checklist + common-password warning.
 *
 * Usage:
 *   <PasswordStrengthMeter value={password} />
 */
export default function PasswordStrengthMeter({ value = '', className = '' }) {
  const { score, label, color, criteria, common } = scorePassword(value);
  if (!value) return null;

  return (
    <div data-testid="password-strength" className={`space-y-2 ${className}`}>
      {/* Segmented strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 flex gap-1">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-1.5 flex-1 rounded-full transition-colors"
              style={{ background: i < score ? color : '#EFF0F6' }}
            />
          ))}
        </div>
        <span
          data-testid="password-strength-label"
          className="text-[11.5px] font-semibold tabular-nums min-w-[68px] text-right"
          style={{ color }}
        >
          {label}
        </span>
      </div>

      {/* Common-password warning */}
      {common && (
        <div
          data-testid="password-common-warning"
          className="text-[11.5px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-2.5 py-1.5 flex items-start gap-1.5"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>Ce mot de passe figure parmi les plus compromis connus. Choisissez-en un autre.</span>
        </div>
      )}

      {/* Criteria checklist */}
      <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11.5px]">
        {criteria.map((c) => (
          <li key={c.key} className="flex items-center gap-1.5">
            {c.met ? (
              <Check className="w-3 h-3 text-[#14B8A6]" />
            ) : (
              <X className="w-3 h-3 text-[#C4C5D4]" />
            )}
            <span className={c.met ? 'text-[#4A4B63]' : 'text-[#8E8FA5]'}>{c.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
